export interface CurriculumRow {
  id: string;
  programme_name: string;
  programme_id: string;
  course_registration_level: string;
  semester: string;
  status: string;
  course_titles: string;
  course_units: string;
  course_codes: string;
  course_instances: string;
  course_id: string;
}

export interface CourseChip {
  code: string;
  title: string;
  unit: string;
  instance: string;
}

export interface CurriculumEntry {
  row: CurriculumRow;
  courses: CourseChip[];
  status: string;
  units: number;
}

export interface SemesterGroup {
  semester: string;
  units: number;
  entries: CurriculumEntry[];
}

export interface LevelGroup {
  level: string;
  semesters: SemesterGroup[];
}

export interface ProgrammeGroup {
  programme: string;
  entryCount: number;
  totalUnits: number;
  levels: LevelGroup[];
}

const splitList = (value: string | number | undefined): string[] =>
  value === undefined || value === null || value === '' ? [] : String(value).split(', ');

/** Explode a row's comma-joined course columns into individual course chips. */
export function coursesFromRow(row: CurriculumRow): CourseChip[] {
  const codes = splitList(row.course_codes);
  const titles = splitList(row.course_titles);
  const units = splitList(row.course_units);
  const instances = splitList(row.course_instances);
  return titles.map((title, i) => ({
    code: codes[i] ?? '',
    title,
    unit: units[i] ?? '',
    instance: instances[i] ?? '',
  }));
}

/** Sum the units across all courses in a single curriculum entry. */
export function courseUnitsSum(row: CurriculumRow): number {
  return splitList(row.course_units).reduce((sum, u) => sum + (Number(u) || 0), 0);
}

const levelRank = (level: string): number => {
  const match = String(level).match(/\d+/);
  return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
};

const semesterRank = (semester: string): number => {
  const lower = String(semester).toLowerCase();
  if (lower.includes('first') || lower.startsWith('1')) return 1;
  if (lower.includes('second') || lower.startsWith('2')) return 2;
  const match = lower.match(/\d+/);
  return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
};

/**
 * Flatten curriculum table rows into a Programme -> Level -> Semester -> entries
 * hierarchy for the grouped (accordion) view. Each entry keeps its original row
 * so edit/delete continue to operate per curriculum entry.
 */
export function groupCurriculumRows(rows: CurriculumRow[]): ProgrammeGroup[] {
  const progMap = new Map<string, Map<string, Map<string, SemesterGroup>>>();
  const progMeta = new Map<string, { entryCount: number; totalUnits: number }>();

  for (const row of rows) {
    const programme = row.programme_name || 'Unassigned';
    const level = row.course_registration_level || '—';
    const semester = row.semester || '—';

    const courses = coursesFromRow(row);
    const entryUnits = courseUnitsSum(row);

    if (!progMap.has(programme)) progMap.set(programme, new Map());
    const levelMap = progMap.get(programme)!;
    if (!levelMap.has(level)) levelMap.set(level, new Map());
    const semMap = levelMap.get(level)!;
    if (!semMap.has(semester)) semMap.set(semester, { semester, units: 0, entries: [] });

    const semGroup = semMap.get(semester)!;
    semGroup.entries.push({ row, courses, status: row.status, units: entryUnits });
    semGroup.units += entryUnits;

    const meta = progMeta.get(programme) ?? { entryCount: 0, totalUnits: 0 };
    meta.entryCount += 1;
    meta.totalUnits += entryUnits;
    progMeta.set(programme, meta);
  }

  return Array.from(progMap.entries()).map(([programme, levelMap]) => ({
    programme,
    entryCount: progMeta.get(programme)?.entryCount ?? 0,
    totalUnits: progMeta.get(programme)?.totalUnits ?? 0,
    levels: Array.from(levelMap.entries())
      .sort((a, b) => levelRank(a[0]) - levelRank(b[0]))
      .map(([level, semMap]) => ({
        level,
        semesters: Array.from(semMap.values()).sort(
          (a, b) => semesterRank(a.semester) - semesterRank(b.semester),
        ),
      })),
  }));
}
